import axios from "axios";
import { Env } from "../enviroment/environment";
import authHeader from "./auth-header";
import {
  readOrendaAuth,
  ensureOrendaTokensFresh,
} from "../components/orenda/orendaAuthStorage";

// Orenda-related calls that go through the Bopayment backend. Every method
// begins with `await ensureOrendaTokensFresh()` — this checks the id_token
// expiry and silently refreshes via the Cognito refresh_token if under a
// 60-second buffer. If refresh fails (refresh_token expired etc.) it throws
// SsoSessionExpired which the calling screen catches to prompt re-auth
// WITHOUT losing form state.
//
// Backend endpoints live in CardHolderController:
//   POST api/cardholder/viewapplicationorenda
//   POST api/cardholder/getkycschemaorenda
//   POST api/cardholder/getlegaldocumentorenda
//   POST api/cardholder/acceptlegaldocumentorenda
//   POST api/cardholder/submitkycdataorenda

const API_URL_Cardholders = Env.CardHolderApiUrl;

// Build the OrendaToken payload the backend expects. When `freshAuth` is
// passed (from ensureOrendaTokensFresh) we use those tokens instead of a
// fresh sessionStorage read — this closes the race where a refresh happens
// mid-call and we'd otherwise send the stale token.
const buildOrendaTokenPayload = (overrides = {}, freshAuth = null) => {
  const auth = freshAuth || readOrendaAuth();
  const user = JSON.parse(sessionStorage.getItem("user") || "null");
  return {
    IdToken: overrides.IdToken ?? auth?.id_token ?? "",
    AccessToken: overrides.AccessToken ?? auth?.access_token ?? "",
    UserId: overrides.UserId ?? user?.user?.UserId ?? "",
    Email: overrides.Email ?? user?.user?.Email ?? "",
  };
};

const viewApplicationOrenda = async (overrides) => {
  const auth = await ensureOrendaTokensFresh();
  const response = await axios.post(
    API_URL_Cardholders + "viewapplicationorenda",
    buildOrendaTokenPayload(overrides, auth),
    { headers: authHeader() }
  );
  return response.data;
};

const getKycSchemaOrenda = async (overrides) => {
  const auth = await ensureOrendaTokensFresh();
  const response = await axios.post(
    API_URL_Cardholders + "getkycschemaorenda",
    buildOrendaTokenPayload(overrides, auth),
    { headers: authHeader() }
  );
  return response.data;
};

// Fetches a Sumsub Web SDK token for the current application. Sumsub's SDK
// takes this as `accessToken` — it's the SDK's per-user session credential
// tied to Orenda's Sumsub configuration. Response wraps { token, expiresAt }
// (millisecond epoch).
const getSumsubTokenOrenda = async (overrides) => {
  const auth = await ensureOrendaTokensFresh();
  const response = await axios.post(
    API_URL_Cardholders + "getsumsubtokenorenda",
    buildOrendaTokenPayload(overrides, auth),
    { headers: authHeader() }
  );
  return response.data;
};

// Creates the applicant's first card after onboarding is complete. Backend
// looks up applicationId / accountId / customerId from the DB using UserId,
// so we only need the token payload. Response wraps CardDataOrenda:
//   { id, customerId, accountId, cardType, status, provider }
const createCardOrenda = async (overrides) => {
  const auth = await ensureOrendaTokensFresh();
  const response = await axios.post(
    API_URL_Cardholders + "createcardorenda",
    buildOrendaTokenPayload(overrides, auth),
    { headers: authHeader() }
  );
  return response.data;
};

// Fetches the set of legal documents the applicant must review + accept for
// the LEGAL_AGREEMENTS onboarding step. Response.data.documents.docs[] is the
// list to render.
const getLegalDocumentOrenda = async (overrides) => {
  const auth = await ensureOrendaTokensFresh();
  const response = await axios.post(
    API_URL_Cardholders + "getlegaldocumentorenda",
    buildOrendaTokenPayload(overrides, auth),
    { headers: authHeader() }
  );
  return response.data;
};

// Submits the applicant's acceptance of a set of legal documents.
// Backend DTO (AcceptDocumentsOrendaRequest) uses camelCase.
const acceptLegalDocumentOrenda = async ({
  documentSetVersion,
  applicationId,
  acceptedDocuments,
  idTokenOverride,
  userIdOverride,
}) => {
  const auth = await ensureOrendaTokensFresh();
  const user = JSON.parse(sessionStorage.getItem("user") || "null");
  const body = {
    documentSetVersion,
    idToken: idTokenOverride ?? auth?.id_token ?? "",
    userId: userIdOverride ?? user?.user?.UserId ?? "",
    applicationId,
    acceptedDocuments,
  };
  const response = await axios.post(
    API_URL_Cardholders + "acceptlegaldocumentorenda",
    body,
    { headers: authHeader() }
  );
  return response.data;
};

// Submits collected KYC answers to the backend.
// Backend DTO (SubmitKycRequest) — camelCase:
//   kycanswers    nested map grouped by step, matching Orenda's schema shape:
//                 { "step-1": { fieldKey: value, ... }, "step-2": {...}, ... }
//                 Flat top-level keys ("employmentStatus" etc.) fail with
//                 "Step '<key>' does not exist in schema".
//   idToken       Cognito id_token
//   accessToken   Cognito access_token
//   userId        Card-Console UserId
//   applicationId from viewApplication response
const submitKycDataOrenda = async ({
  kycanswers,
  applicationId,
  idTokenOverride,
  accessTokenOverride,
  userIdOverride,
}) => {
  const auth = await ensureOrendaTokensFresh();
  const user = JSON.parse(sessionStorage.getItem("user") || "null");
  const body = {
    kycanswers,
    idToken: idTokenOverride ?? auth?.id_token ?? "",
    accessToken: accessTokenOverride ?? auth?.access_token ?? "",
    userId: userIdOverride ?? user?.user?.UserId ?? "",
    applicationId,
  };
  const response = await axios.post(
    API_URL_Cardholders + "submitkycdataorenda",
    body,
    { headers: authHeader() }
  );
  return response.data;
};

// ─────────────────────────────────────────────────────────────────
// Secure card details — Orenda's two-step (or three-step with
// passkey) flow for revealing PAN / CVC / expiry. Docs:
//   https://api-docs.orenda-apps.com/api-reference/secure-details/get-secure-key/
//
// Step 1: getPassKeySessionOrenda → backend wraps Orenda's /secure-key
//   (and may chain /passkey-challenge) and returns the cipher plus
//   WebAuthn fido2options we feed to navigator.credentials.get().
// Step 2: user approves the passkey → produces a WebAuthn assertion
// Step 3: getCardSecureDetailsOrenda → sends cipher + passkeySession
//   + assertion and gets back the actual card details.
// ─────────────────────────────────────────────────────────────────

const getPassKeySessionOrenda = async (overrides) => {
  const auth = await ensureOrendaTokensFresh();
  const response = await axios.post(
    API_URL_Cardholders + "getpasskeysession",
    buildOrendaTokenPayload(overrides, auth),
    { headers: authHeader() }
  );
  return response.data;
};

// ─────────────────────────────────────────────────────────────────
// Passkey enrollment — adds a new passkey to the signed-in user so
// they can confirm sensitive actions (viewing PAN/CVC). SSO users:
// the first startAddPasskey call returns EMAIL_VERIFICATION_REQUIRED
// and Orenda emails a 6-digit code; the second call takes the code.
// Then the browser does navigator.credentials.create() and we send
// the attestation to finishAddPasskey.
// ─────────────────────────────────────────────────────────────────

// v2 field names: friendlyName / emailCode (camelCase). Our backend DTO
// accepts both v1 and v2 for the brief cutover window, but we always send
// v2 going forward.
const startAddPasskeyOrenda = async ({ friendlyName, emailCode } = {}) => {
  const auth = await ensureOrendaTokensFresh();
  const user = JSON.parse(sessionStorage.getItem("user") || "null");
  const body = {
    AccessToken: auth?.access_token ?? "",
    UserId: user?.user?.UserId ?? "",
    friendlyName: friendlyName || null,
    emailCode: emailCode || null,
  };
  const response = await axios.post(
    API_URL_Cardholders + "startaddpasskeyorenda",
    body,
    { headers: authHeader() }
  );
  return response.data;
};

// v2 shape: body now takes `sessionToken` and the pre-flattened `attestation`
// ({ credentialIdB64, clientDataJSON_B64, attestationObjectB64, transports }).
const finishAddPasskeyOrenda = async ({ sessionToken, credential }) => {
  const auth = await ensureOrendaTokensFresh();
  const user = JSON.parse(sessionStorage.getItem("user") || "null");
  const body = {
    AccessToken: auth?.access_token ?? "",
    UserId: user?.user?.UserId ?? "",
    sessionToken,
    attestation: credential,
  };
  const response = await axios.post(
    API_URL_Cardholders + "finishaddpasskeyorenda",
    body,
    { headers: authHeader() }
  );
  return response.data;
};

const listPasskeysOrenda = async () => {
  const auth = await ensureOrendaTokensFresh();
  const user = JSON.parse(sessionStorage.getItem("user") || "null");
  const body = {
    AccessToken: auth?.access_token ?? "",
    UserId: user?.user?.UserId ?? "",
  };
  const response = await axios.post(
    API_URL_Cardholders + "listpasskeysorenda",
    body,
    { headers: authHeader() }
  );
  return response.data;
};

// Backend DTO (GetSecureDetailsOrenda) — camelCase:
//   AccessToken, UserId, cipher, assertion, passkeySession
const getCardSecureDetailsOrenda = async ({
  cipher,
  assertion,
  passkeySession,
  accessTokenOverride,
  userIdOverride,
}) => {
  const auth = await ensureOrendaTokensFresh();
  const user = JSON.parse(sessionStorage.getItem("user") || "null");
  const body = {
    AccessToken: accessTokenOverride ?? auth?.access_token ?? "",
    UserId: userIdOverride ?? user?.user?.UserId ?? "",
    cipher,
    assertion,
    passkeySession,
  };
  const response = await axios.post(
    API_URL_Cardholders + "getcardsecuredetails",
    body,
    { headers: authHeader() }
  );
  return response.data;
};

const orendaService = {
  viewApplicationOrenda,
  getKycSchemaOrenda,
  getSumsubTokenOrenda,
  getLegalDocumentOrenda,
  acceptLegalDocumentOrenda,
  submitKycDataOrenda,
  createCardOrenda,
  getPassKeySessionOrenda,
  getCardSecureDetailsOrenda,
  startAddPasskeyOrenda,
  finishAddPasskeyOrenda,
  listPasskeysOrenda,
  buildOrendaTokenPayload,
};

export default orendaService;
